import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useApiClient } from '../../hooks/useApiClient'
import { useTheme } from '../../hooks/useTheme'
import { toastError, toastSuccess } from '../../utils/toastHelper'
import TipoEspacioPlanoMapper from '../../components/admin/TipoEspacioPlanoMapper'
import TarifasManager from '../../components/admin/TarifasManager'
import ImageUploader from '../../components/common/ImageUploader'
import { ActionButton, ModalButton, ConfirmDeleteModal } from '../../components/ui'

interface Comodidad {
  icono: string
  nombre: string
}

interface TipoEspacio {
  id: string
  nombre: string
  descripcion: string
  capacidad_maxima: number
  tarifa_base_tipo: number
  tarifa_base_tipo_multiple?: number
  unidad_reserva: string
  caracteristicas_requeridas: Record<string, any>
  comodidades: Comodidad[] | null
  indicaciones: Record<string, any> | null
  instrucciones: string | null
  plano_vertices: Array<{ x: number; y: number }> | null
  reservable: boolean
  privado: boolean
  imagen: string | null
}

interface TipoEspacioForm {
  nombre: string
  descripcion: string
  capacidad_maxima: number
  tarifa_base_tipo: number
  tarifa_base_tipo_multiple?: number
  unidad_reserva: string
  caracteristicas_requeridas: Record<string, any>
  comodidades: Comodidad[]
  indicaciones: any | null // Puede ser rectangle, circle o polygon
  instrucciones: string
  reservable: boolean
  privado: boolean
}

// Lista de comodidades predefinidas con emojis
const COMODIDADES_DISPONIBLES: Comodidad[] = [
  { icono: '⚡', nombre: 'Electricidad' },
  { icono: '💧', nombre: 'Agua' },
  { icono: '🚿', nombre: 'Baños compartidos' },
  { icono: '🚽', nombre: 'Baño privado' },
  { icono: '🛁', nombre: 'Duchas' },
  { icono: '🛌', nombre: 'Cama' },
  { icono: '🛏️', nombre: 'Ropa de cama' },
  { icono: '📶', nombre: 'Wi-Fi' },
  { icono: '🅿️', nombre: 'Estacionamiento' },
  { icono: '🍳', nombre: 'Cocina compartida' },
  { icono: '🔥', nombre: 'Fogón/Parrilla' },
  { icono: '🏊', nombre: 'Piscina' },
  { icono: '🐕', nombre: 'Mascotas permitidas' },
  { icono: '❄️', nombre: 'Aire acondicionado' },
  { icono: '🔥', nombre: 'Calefacción' },
  { icono: '📺', nombre: 'TV' },
  { icono: '🧺', nombre: 'Lavandería' },
  { icono: '🍽️', nombre: 'Restaurante' },
  { icono: '🏪', nombre: 'Minimarket' },
  { icono: '🔒', nombre: 'Seguridad 24/7' },
  { icono: '♿', nombre: 'Accesibilidad' },
  { icono: '🏖️', nombre: 'Acceso a playa' },
]

const AdminTiposEspacios: React.FC = () => {
  const { permissions } = useAuth()
  const { colors } = useTheme()
  const { apiClient } = useApiClient()
  const [tiposEspacios, setTiposEspacios] = useState<TipoEspacio[]>([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ id: string; nombre: string } | null>(null)
  const [selectedComodidad, setSelectedComodidad] = useState<string>('')
  const [form, setForm] = useState<TipoEspacioForm>({
    nombre: '',
    descripcion: '',
    capacidad_maxima: 1,
    tarifa_base_tipo: 0,
    tarifa_base_tipo_multiple: undefined,
    unidad_reserva: 'noche',
    caracteristicas_requeridas: {},
    comodidades: [],
    indicaciones: null,
    instrucciones: '',
    reservable: true,
    privado: false
  })

  const hasPermission = (permission: string): boolean => {
    return permissions?.includes(permission) || false
  }

  // Cargar tipos de espacios
  useEffect(() => {
    loadTiposEspacios()
  }, [])

  const loadTiposEspacios = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/api/admin-protected/tipos-espacios?limite=1000&offset=0')
      const data = response.data || []
      setTiposEspacios(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error loading tipos espacios:', error)
      toastError('Error al cargar tipos de espacios')
      setTiposEspacios([])
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setForm({
      nombre: '',
      descripcion: '',
      capacidad_maxima: 1,
      tarifa_base_tipo: 0,
      tarifa_base_tipo_multiple: undefined,
      unidad_reserva: 'noche',
      caracteristicas_requeridas: {},
      comodidades: [],
      indicaciones: null,
      instrucciones: '',
      reservable: true,
      privado: false
    })
    setEditingId(null)
  }

  const handleNew = () => {
    if (!hasPermission('tipo_espacio.crear')) {
      toastError('No tienes permisos para crear tipos de espacios')
      return
    }
    resetForm()
    setShowForm(true)
  }

  const handleEdit = (tipo: TipoEspacio) => {
    if (!hasPermission('tipo_espacio.actualizar')) {
      toastError('No tienes permisos para editar tipos de espacios')
      return
    }

    setForm({
      nombre: tipo.nombre,
      descripcion: tipo.descripcion,
      capacidad_maxima: tipo.capacidad_maxima,
      tarifa_base_tipo: tipo.tarifa_base_tipo,
      tarifa_base_tipo_multiple: tipo.tarifa_base_tipo_multiple,
      unidad_reserva: tipo.unidad_reserva,
      caracteristicas_requeridas: tipo.caracteristicas_requeridas || {},
      comodidades: tipo.comodidades || [],
      indicaciones: tipo.indicaciones,
      instrucciones: tipo.instrucciones || '',
      reservable: tipo.reservable ?? true, // Maneja null y undefined
      privado: tipo.privado ?? false
    })
    setEditingId(tipo.id)
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!form.nombre.trim()) {
      toastError('El nombre del tipo de espacio es requerido')
      return
    }

    if (editingId && !hasPermission('tipo_espacio.actualizar')) {
      toastError('No tienes permisos para actualizar tipos de espacios')
      return
    }

    if (!editingId && !hasPermission('tipo_espacio.crear')) {
      toastError('No tienes permisos para crear tipos de espacios')
      return
    }

    setLoading(true)
    try {
      if (editingId) {
        // Actualizar
        await apiClient.put(`/api/admin-protected/tipos-espacios/${editingId}`, form)
        toastSuccess('Tipo de espacio actualizado')
      } else {
        // Crear
        await apiClient.post('/api/admin-protected/tipos-espacios', form)
        toastSuccess('Tipo de espacio creado')
      }
      setShowForm(false)
      resetForm()
      await loadTiposEspacios()
    } catch (error) {
      console.error('Error saving tipo espacio:', error)
      toastError('Error al guardar tipo de espacio')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = (id: string, nombre: string) => {
    if (!hasPermission('tipo_espacio.eliminar')) {
      toastError('No tienes permisos para eliminar tipos de espacios')
      return
    }
    setItemToDelete({ id, nombre })
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return

    setLoading(true)
    try {
      await apiClient.delete(`/api/admin-protected/tipos-espacios/${itemToDelete.id}`)
      toastSuccess('Tipo de espacio eliminado')
      setShowDeleteConfirm(false)
      setItemToDelete(null)
      await loadTiposEspacios()
    } catch (error) {
      console.error('Error deleting tipo espacio:', error)
      toastError('Error al eliminar tipo de espacio')
    } finally {
      setLoading(false)
    }
  }

  const closeForm = () => {
    setShowForm(false)
    resetForm()
  }

  const handleIndicacionesChange = useCallback((indicaciones: any) => {
    setForm(prev => ({
      ...prev,
      indicaciones
    }))
  }, [])

  if (!hasPermission('tipo_espacio.ver')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Sin permisos</h3>
          <p className="text-gray-600">No tienes permisos para ver los tipos de espacios</p>
        </div>
      </div>
    )
  }

  if (loading && tiposEspacios.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 rounded-full mx-auto mb-4" style={{ borderColor: `${colors.primary}40`, borderTopColor: colors.primary }}></div>
          <p className="text-gray-600">Cargando tipos de espacios...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Tipos de Espacios</h2>
                <p className="text-gray-600">Gestiona los tipos de espacios disponibles</p>
              </div>
              <div className="flex gap-3">
                {hasPermission('tipo_espacio.crear') && (
                  <button
                    onClick={handleNew}
                    className="px-8 py-4 text-lg rounded-xl font-semibold text-white transition-all duration-300 ease-out cursor-pointer"
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primaryDark
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary
                    }}
                  >
                    ➕ Nuevo Tipo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Lista de Tipos de Espacios */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Tipos de Espacios ({tiposEspacios.length})
              </h3>
            </div>

            {tiposEspacios.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🏠</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay tipos de espacios</h3>
                <p className="text-gray-600 mb-4">
                  {hasPermission('tipo_espacio.crear') ? 'Crea el primer tipo de espacio para empezar' : 'No hay tipos de espacios disponibles'}
                </p>
                {hasPermission('tipo_espacio.crear') && (
                  <button
                    onClick={handleNew}
                    className="px-8 py-4 text-lg rounded-xl font-semibold text-white transition-all duration-300 ease-out cursor-pointer"
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primaryDark
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary
                    }}
                  >
                    ➕ Crear Tipo de Espacio
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cap</th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">T.Simple</th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">T.Múlt</th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Unid</th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comodidades</th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Res</th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Priv</th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Ind</th>
                      <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(tiposEspacios || []).map((tipo) => (
                      <tr key={tipo.id} className="hover:bg-gray-50">
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 max-w-[120px] truncate">{tipo.nombre}</div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-xs text-gray-600 max-w-[150px] truncate" title={tipo.descripcion || '-'}>{tipo.descripcion || '-'}</div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-center">
                          <div className="text-xs text-gray-900">{tipo.capacidad_maxima}</div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs text-gray-900">${tipo.tarifa_base_tipo.toLocaleString()}</div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs text-gray-900">{tipo.tarifa_base_tipo_multiple ? `$${tipo.tarifa_base_tipo_multiple.toLocaleString()}` : '-'}</div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-center">
                          <div className="text-xs text-gray-900">{tipo.unidad_reserva === 'noche' ? 'N' : tipo.unidad_reserva === 'dia' ? 'D' : 'H'}</div>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex flex-wrap gap-1 max-w-[100px]">
                            {tipo.comodidades && tipo.comodidades.length > 0 ? (
                              tipo.comodidades.slice(0, 4).map((comodidad, idx) => (
                                <span
                                  key={idx}
                                  style={{ fontSize: '16px' }}
                                  title={comodidad.nombre}
                                >
                                  {comodidad.icono}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                            {tipo.comodidades && tipo.comodidades.length > 4 && (
                              <span className="text-xs text-gray-500">+{tipo.comodidades.length - 4}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-center">
                          <span className="text-lg">
                            {tipo.reservable !== false ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-center">
                          <span className="text-lg">
                            {tipo.privado === true ? '🔒' : '🌐'}
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-center">
                          <div className="text-lg">
                            {tipo.indicaciones ? '✅' : '❌'}
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-center">
                          <div className="flex justify-center space-x-1">
                            {hasPermission('tipo_espacio.actualizar') && (
                              <ActionButton
                                type="edit"
                                onClick={() => handleEdit(tipo)}
                                title="Editar tipo de espacio"
                              />
                            )}
                            {hasPermission('tipo_espacio.eliminar') && (
                              <ActionButton
                                type="delete"
                                onClick={() => handleDelete(tipo.id, tipo.nombre)}
                                title="Eliminar tipo de espacio"
                              />
                            )}
                            {!hasPermission('tipo_espacio.actualizar') && !hasPermission('tipo_espacio.eliminar') && (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  {editingId ? 'Editar Tipo de Espacio' : 'Nuevo Tipo de Espacio'}
                </h2>
                <p className="text-gray-600 text-sm">
                  {editingId ? 'Actualiza la información del tipo de espacio' : 'Crea un nuevo tipo de espacio'}
                </p>
              </div>

              {/* Información Básica */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Información Básica</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                    <input
                      type="text"
                      required
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ focusRingColor: colors.primary }}
                      placeholder="Ej: Cabaña Premium"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                    <textarea
                      value={form.descripcion}
                      onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                      rows={3}
                      placeholder="Descripción del tipo de espacio..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Capacidad Máxima *</label>
                      <input
                        type="number"
                        required
                        min="1"
                        value={form.capacidad_maxima}
                        onChange={(e) => setForm({ ...form, capacidad_maxima: parseInt(e.target.value) || 1 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Unidad de Reserva</label>
                      <select
                        value={form.unidad_reserva}
                        onChange={(e) => setForm({ ...form, unidad_reserva: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="noche">Noche</option>
                        <option value="dia">Día</option>
                        <option value="hora">Hora</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="reservable"
                      checked={form.reservable}
                      onChange={(e) => setForm({ ...form, reservable: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 cursor-pointer"
                      style={{ accentColor: colors.primary }}
                    />
                    <div className="flex-1">
                      <label htmlFor="reservable" className="text-sm font-medium text-gray-900 cursor-pointer">
                        Reservable
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Permite que este tipo de espacio sea reservable desde el sitio público
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <input
                      type="checkbox"
                      id="privado"
                      checked={form.privado}
                      onChange={(e) => setForm({ ...form, privado: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 cursor-pointer"
                      style={{ accentColor: colors.primary }}
                    />
                    <div className="flex-1">
                      <label htmlFor="privado" className="text-sm font-medium text-gray-900 cursor-pointer">
                        Privado
                      </label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Si está marcado, este tipo NO se mostrará en el sitio público (uso interno)
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tarifa Base (Simple) *</label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={form.tarifa_base_tipo}
                        onChange={(e) => setForm({ ...form, tarifa_base_tipo: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tarifa Base (Múltiple) <span className="text-gray-500">(Opcional)</span></label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.tarifa_base_tipo_multiple || ''}
                        onChange={(e) => setForm({ ...form, tarifa_base_tipo_multiple: e.target.value ? parseFloat(e.target.value) : undefined })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Dejar vacío si no aplica"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Instrucciones de Acceso</label>
                    <textarea
                      value={form.instrucciones}
                      onChange={(e) => setForm({ ...form, instrucciones: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                      rows={3}
                      placeholder="Instrucciones para acceder al espacio..."
                    />
                  </div>
                </div>
              </div>

              {/* Comodidades */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🏷️ Comodidades</h3>
                <div className="space-y-3">
                  {/* Lista de comodidades */}
                  {form.comodidades.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {form.comodidades.map((comodidad, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs border"
                          style={{
                            backgroundColor: `${colors.primary}15`,
                            borderColor: colors.primary,
                            color: colors.primary,
                            gap: '0.375rem'
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>
                            {comodidad.icono}
                          </span>
                          <span>{comodidad.nombre}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setForm({
                                ...form,
                                comodidades: form.comodidades.filter((_, i) => i !== index)
                              })
                            }}
                            className="ml-1 hover:text-red-600"
                            title="Eliminar comodidad"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Agregar nueva comodidad desde selector */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Agregar comodidad</p>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <select
                          value={selectedComodidad}
                          onChange={(e) => setSelectedComodidad(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">Selecciona una comodidad...</option>
                          {COMODIDADES_DISPONIBLES.map((comodidad, idxOriginal) => {
                            // Verificar si ya está agregada
                            const yaAgregada = form.comodidades.some(fc => fc.icono === comodidad.icono)
                            if (yaAgregada) return null

                            return (
                              <option key={idxOriginal} value={idxOriginal}>
                                {comodidad.nombre}
                              </option>
                            )
                          })}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!selectedComodidad) {
                            toastError('Selecciona una comodidad')
                            return
                          }

                          const comodidad = COMODIDADES_DISPONIBLES[parseInt(selectedComodidad)]

                          setForm({
                            ...form,
                            comodidades: [...form.comodidades, comodidad]
                          })

                          setSelectedComodidad('')
                        }}
                        disabled={!selectedComodidad}
                        className="px-4 py-2 text-sm rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: selectedComodidad ? colors.primary : '#9ca3af' }}
                        onMouseEnter={(e) => {
                          if (selectedComodidad) {
                            e.currentTarget.style.backgroundColor = colors.primaryDark
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedComodidad) {
                            e.currentTarget.style.backgroundColor = colors.primary
                          }
                        }}
                      >
                        ➕ Agregar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Imagen del Tipo */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📸 Imagen del Tipo</h3>
                {editingId ? (
                  <ImageUploader
                    key={`image-uploader-${editingId}-${tiposEspacios.find(t => t.id === editingId)?.imagen || 'none'}`}
                    mode="single"
                    images={tiposEspacios.find(t => t.id === editingId)?.imagen ? [tiposEspacios.find(t => t.id === editingId)!.imagen!] : []}
                    uploadEndpoint={`/api/admin-protected/tipos-espacios/${editingId}/upload-image`}
                    deleteEndpoint={`/api/admin-protected/tipos-espacios/${editingId}/image`}
                    onImagesUpdated={async () => {
                      // Refrescar la lista completa para actualizar el tipo editado
                      await loadTiposEspacios()
                    }}
                    buttonText="Subir Imagen"
                  />
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      💡 Primero guarda el tipo de espacio para poder agregar una imagen
                    </p>
                  </div>
                )}
              </div>

              {/* Mapeo de Ubicación */}
              <div className="border-t pt-6">
                <TipoEspacioPlanoMapper
                  planoImageUrl={`${import.meta.env.VITE_API_URL}/static/uploads/planos/plano-camping.png`}
                  tipoEspacioNombre={form.nombre || 'Sin nombre'}
                  defaultExpanded={true}
                  initialValues={form.indicaciones}
                  onIndicacionesChange={handleIndicacionesChange}
                />
              </div>

              {/* Gestión de Tarifas */}
              {editingId && (
                <div className="border-t pt-6">
                  <TarifasManager
                    tipoEspacioId={editingId}
                    unidad_reserva={form.unidad_reserva}
                  />
                </div>
              )}

              {/* Botones */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <ModalButton
                  variant="secondary"
                  type="button"
                  onClick={closeForm}
                >
                  ❌ Cancelar
                </ModalButton>
                <ModalButton
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  loading={loading}
                >
                  {editingId ? '✏️ Actualizar' : '➕ Crear'}
                </ModalButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        title="¿Eliminar tipo de espacio?"
        message="¿Estás seguro que deseas eliminar este tipo de espacio?"
        itemName={itemToDelete?.nombre}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false)
          setItemToDelete(null)
        }}
        isLoading={loading}
      />
    </div>
  )
}

export default AdminTiposEspacios
